import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { catalogueIntegrationsApi, shopifyApi } from '../lib/api';
import { Header } from '../components/Header';
import { LoadingSpinner } from '../components/LoadingSpinner';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent } from '../components/ui/card';
import { Switch } from '../components/ui/switch';
import { toast } from 'sonner';
import {
    ArrowLeft, ArrowRight, Check, ExternalLink, X, Plug,
    ShoppingBag, BarChart3, ShoppingCart, FileSpreadsheet,
    Code2, FileUp, Loader2, CircleDot, Trash2, AlertCircle, Package, RefreshCw,
    Settings2, Plus
} from 'lucide-react';

// ==================== Constants ====================

const PROVIDERS = [
    { id: 'shopify', name: 'Shopify', desc: 'E-commerce', initials: 'SH', color: 'bg-emerald-100 text-emerald-700', brand: '#5E8E3E' },
    { id: 'linnworks', name: 'Linnworks', desc: 'Inventory mgmt.', initials: 'LW', color: 'bg-sky-100 text-sky-700', brand: '#0EA5E9' },
    { id: 'woocommerce', name: 'WooCommerce', desc: 'E-commerce', initials: 'WC', color: 'bg-orange-100 text-orange-700', brand: '#7F54B3' },
    { id: 'google_sheets', name: 'Google Sheets', desc: 'Spreadsheet', initials: 'GS', color: 'bg-violet-100 text-violet-700', brand: '#34A853' },
];

const ALT_METHODS = [
    { id: 'rest_api', name: 'REST API', desc: 'Webhook or polling', icon: Code2 },
    { id: 'csv', name: 'CSV Import', desc: 'Upload manually', icon: FileUp },
    { id: 'owntune', name: 'Owntune Inventory', desc: 'Track stock directly in Owntune', icon: Package },
];

const STEP_LABELS = ['Choose source', 'Connect', 'Match fields', 'Rules'];

const OWNTUNE_FIELDS = [
    { key: 'title', label: 'Title' },
    { key: 'catalogue_number', label: 'Catalogue #' },
    { key: 'artist', label: 'Artist' },
    { key: 'stock_quantity', label: 'Stock quantity' },
    { key: 'media_type', label: 'Media type' },
    { key: 'cover_image', label: 'Cover image' },
    { key: 'format', label: 'Format' },
    { key: 'sony_master_ref', label: 'Master reference' },
];

// ==================== Stepper ====================

const Stepper = ({ currentStep, steps }) => (
    <div className="flex items-center justify-center gap-0 mb-10" data-testid="integration-stepper">
        {steps.map((label, i) => {
            const stepNum = i + 1;
            const isCompleted = currentStep > stepNum;
            const isActive = currentStep === stepNum;
            return (
                <div key={i} className="flex items-center">
                    {i > 0 && (
                        <div className={`w-16 sm:w-24 h-[3px] mx-1.5 rounded-full transition-colors duration-300 ${
                            isCompleted ? 'bg-emerald-500' : isActive ? 'bg-indigo-500' : 'bg-gray-200'
                        }`} />
                    )}
                    <div className="flex items-center gap-2.5">
                        <div
                            className={`
                                rounded-full flex items-center justify-center font-semibold shrink-0 transition-all duration-300
                                ${isActive ? 'w-10 h-10 text-base shadow-md shadow-indigo-200 ring-4 ring-indigo-100 bg-indigo-600 text-white' : ''}
                                ${isCompleted ? 'w-8 h-8 text-sm bg-emerald-500 text-white shadow-sm' : ''}
                                ${!isActive && !isCompleted ? 'w-8 h-8 text-sm bg-gray-100 text-gray-400 border border-gray-200' : ''}
                            `}
                        >
                            {isCompleted ? <Check className="h-4 w-4 animate-in fade-in zoom-in duration-200" /> : stepNum}
                        </div>
                        <span className={`text-sm hidden sm:inline whitespace-nowrap transition-colors ${
                            isActive ? 'font-semibold text-gray-900' : isCompleted ? 'text-emerald-700' : 'text-gray-400'
                        }`}>
                            {label}
                        </span>
                    </div>
                </div>
            );
        })}
    </div>
);

// ==================== Step 1: Choose Source ====================

const StepChooseSource = ({ selected, onSelect }) => (
    <div className="space-y-8" data-testid="step-choose-source">
        <div>
            <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-4">Direct integrations</h3>
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4">
                {PROVIDERS.map(p => {
                    const isSelected = selected === p.id;
                    const isDeselected = selected && !isSelected;
                    const accentBorder = isSelected
                        ? p.id === 'shopify' ? 'border-[#5E8E3E]' : 'border-indigo-500'
                        : 'border-gray-100';
                    const accentBg = isSelected
                        ? p.id === 'shopify' ? 'bg-emerald-50/50' : 'bg-white'
                        : 'bg-white';
                    return (
                        <button
                            key={p.id}
                            onClick={() => onSelect(p.id)}
                            data-testid={`provider-${p.id}`}
                            className={`
                                relative flex items-center gap-3 p-4 rounded-xl border-2 transition-all text-left
                                ${accentBorder} ${accentBg}
                                ${isSelected ? 'shadow-md ring-2 ring-offset-1 ' + (p.id === 'shopify' ? 'ring-emerald-200' : 'ring-indigo-100') : 'hover:border-gray-200 hover:shadow-sm'}
                                ${isDeselected ? 'opacity-50' : ''}
                            `}
                        >
                            {isSelected && (
                                <div
                                    className="absolute -top-2 -right-2 h-5 w-5 rounded-full bg-emerald-500 text-white flex items-center justify-center shadow"
                                    data-testid={`provider-${p.id}-selected`}
                                    aria-label="Selected"
                                >
                                    <Check className="h-3 w-3" />
                                </div>
                            )}
                            <div className={`w-10 h-10 rounded-lg flex items-center justify-center text-sm font-bold shrink-0 ${p.color}`}>
                                {p.initials}
                            </div>
                            <div className="min-w-0">
                                <div className="font-semibold text-sm text-gray-900 whitespace-nowrap">{p.name}</div>
                                <div className="text-xs text-gray-400 whitespace-nowrap">{p.desc}</div>
                                {isSelected && (
                                    <span className={`inline-flex items-center gap-1 mt-1 text-[10px] font-semibold px-2 py-0.5 rounded-full ${
                                        p.id === 'shopify' ? 'bg-[#5E8E3E] text-white' : 'bg-indigo-600 text-white'
                                    }`}>
                                        <Check className="h-2.5 w-2.5" /> Selected
                                    </span>
                                )}
                            </div>
                        </button>
                    );
                })}
            </div>
        </div>

        <div>
            <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-4">Or use API / CSV</h3>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                {ALT_METHODS.map(m => {
                    const Icon = m.icon;
                    const isSelected = selected === m.id;
                    const isDeselected = selected && !isSelected;
                    return (
                        <button
                            key={m.id}
                            onClick={() => onSelect(m.id)}
                            data-testid={`provider-${m.id}`}
                            className={`
                                relative flex items-center gap-4 p-4 rounded-xl border-2 transition-all text-left
                                ${isSelected
                                    ? 'border-indigo-500 bg-white shadow-md ring-2 ring-offset-1 ring-indigo-100'
                                    : 'border-gray-100 bg-white hover:border-gray-200 hover:shadow-sm'
                                }
                                ${isDeselected ? 'opacity-50' : ''}
                            `}
                        >
                            {isSelected && (
                                <div className="absolute -top-2 -right-2 h-5 w-5 rounded-full bg-emerald-500 text-white flex items-center justify-center shadow">
                                    <Check className="h-3 w-3" />
                                </div>
                            )}
                            <div className="w-10 h-10 rounded-lg bg-gray-50 flex items-center justify-center shrink-0">
                                <Icon className="h-5 w-5 text-gray-500" />
                            </div>
                            <div className="min-w-0">
                                <div className="font-semibold text-sm text-gray-900 whitespace-nowrap">{m.name}</div>
                                <div className="text-xs text-gray-400 whitespace-nowrap">{m.desc}</div>
                            </div>
                        </button>
                    );
                })}
            </div>
        </div>
    </div>
);

// ==================== Step 2: Connect ====================

// Inline Shopify wordmark/logo (SVG, public domain shape) — used in connect & callback
const ShopifyLogo = ({ className = "h-5 w-5" }) => (
    <svg viewBox="0 0 24 24" className={className} aria-hidden="true">
        <path
            fill="#95BF47"
            d="M15.32 4.72c-.06-.04-.12-.06-.18-.06-.06-.06-1.32-.12-1.32-.12s-1.02-1.02-1.14-1.14c-.12-.12-.34-.08-.42-.06l-.58.18c-.06-.18-.16-.4-.3-.62-.42-.8-1.06-1.22-1.78-1.22h-.04c-.06 0-.1.02-.16.04C9.34 1.66 9.26 1.5 9.16 1.4c-.46-.5-1.16-.74-2-.7-1.66.04-3.32 1.24-4.66 3.36C1.56 5.62.92 7.42.78 8.84.04 11.18.62 12.18.74 12.34l3.5 1.36 1.34 9.74 6.62-1.42-2.88-15.3z"
        />
        <path
            fill="#5E8E3E"
            d="M15.14 4.66c-.06.02-.12.04-.18.06l-1 .3c-.04-.5-.16-1.18-.4-1.86-.78.14-1.56.74-2.06 1.46.36-.1.74-.22 1.16-.34.32-.84.84-1.74 1.78-1.74h.06c.28 0 .56.1.74.4-.4.06-.66.34-.66.74-.02.4.18.74.56.74h.04l-.04.24z"
        />
        <path
            fill="#FFF"
            d="M14.94 4.66l-1.04 5.5c-.18-.06-.46-.18-.66-.3-.18-.1-.5-.34-.84-.66-.34-.32-.6-.6-.84-1.04-.24-.42-.4-.96-.4-1.6 0-.4.06-.78.16-1.08.18-.62.5-1.04.74-1.32.36-.42.74-.7 1.16-.86.4-.16.78-.18 1.06-.16.34.04.62.18.66.5z"
        />
    </svg>
);

const ConnectingOverlay = ({ message = "Redirecting to Shopify…" }) => (
    <div
        className="fixed inset-0 z-50 bg-white/95 backdrop-blur flex items-center justify-center"
        data-testid="shopify-connecting-overlay"
    >
        <div className="text-center space-y-5 max-w-sm px-6">
            <div className="mx-auto w-16 h-16 rounded-2xl bg-[#5E8E3E]/10 flex items-center justify-center">
                <ShopifyLogo className="h-9 w-9" />
            </div>
            <Loader2 className="h-8 w-8 mx-auto text-[#5E8E3E] animate-spin" />
            <h3 className="text-lg font-semibold text-gray-900">{message}</h3>
            <p className="text-sm text-gray-500">
                You'll approve the requested scopes on Shopify and return automatically.
                Don't close this window.
            </p>
        </div>
    </div>
);

const ShopifyConnectStep = ({ credentials, updateField, shopifyStatus, setShopifyStatus, isPreviewEnv }) => {
    const [connecting, setConnecting] = useState(false);
    const [disconnecting, setDisconnecting] = useState(false);
    const isConnected = !!shopifyStatus?.connected;

    const validateShop = (raw) => {
        const v = (raw || '').trim().toLowerCase();
        if (!v) return 'Enter your Shopify store URL';
        if (v.startsWith('http')) return 'Drop the https:// prefix';
        if (!v.endsWith('.myshopify.com')) return 'Must be a *.myshopify.com host';
        return null;
    };

    const handleConnect = async (override) => {
        const shop = (override || credentials.store_url || '').trim().toLowerCase().replace(/^https?:\/\//, '');
        const err = validateShop(shop);
        if (err) {
            toast.error(err);
            return;
        }
        setConnecting(true);
        try {
            const res = await shopifyApi.oauthInit(shop);
            const { authorize_url } = res.data || {};
            if (!authorize_url) throw new Error('No authorize URL returned');
            sessionStorage.setItem('shopify_oauth_shop', shop);
            // Brief delay so the user sees the loading state before redirect
            setTimeout(() => { window.location.href = authorize_url; }, 350);
        } catch (e) {
            toast.error(e.response?.data?.detail || 'Could not start Shopify OAuth');
            setConnecting(false);
        }
    };

    const handleDisconnect = async () => {
        if (!window.confirm('Disconnect Shopify? You will need to re-authorize to sync inventory again.')) return;
        setDisconnecting(true);
        try {
            await shopifyApi.disconnect();
            toast.success('Shopify disconnected');
            setShopifyStatus({ connected: false });
            updateField('store_url', '');
        } catch (e) {
            toast.error(e.response?.data?.detail || 'Could not disconnect');
        } finally {
            setDisconnecting(false);
        }
    };

    const handleReconnect = () => {
        if (shopifyStatus?.shop_domain) {
            handleConnect(shopifyStatus.shop_domain);
        }
    };

    if (connecting) return <ConnectingOverlay />;

    if (isConnected) {
        const scopeList = (shopifyStatus.scopes || 'read_products,read_inventory,write_inventory,read_locations')
            .split(',').map(s => s.trim()).filter(Boolean);
        return (
            <div className="space-y-5 max-w-2xl" data-testid="shopify-connected">
                {/* Hero success card — Stripe / Linear / Vercel feel */}
                <div className="rounded-2xl border-2 border-emerald-200 bg-gradient-to-br from-emerald-50/80 to-white p-7 shadow-sm">
                    <div className="flex items-start gap-4">
                        <div className="shrink-0 w-14 h-14 rounded-xl bg-white border border-emerald-200 shadow-sm flex items-center justify-center">
                            <ShopifyLogo className="h-8 w-8" />
                        </div>
                        <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2">
                                <Check className="h-5 w-5 text-emerald-600" />
                                <span className="text-base font-semibold text-emerald-900">
                                    Shopify connected
                                </span>
                                {isPreviewEnv && (
                                    <span className="text-[10px] font-semibold uppercase tracking-wide px-1.5 py-0.5 rounded bg-amber-100 text-amber-800 border border-amber-200">
                                        Preview env
                                    </span>
                                )}
                            </div>
                            <div className="mt-3 space-y-2 text-sm">
                                <div className="flex flex-wrap items-baseline gap-x-2">
                                    <span className="text-gray-500 w-32 shrink-0">Store</span>
                                    <span className="font-mono text-gray-900 font-semibold">{shopifyStatus.shop_domain}</span>
                                </div>
                                <div className="flex flex-wrap items-baseline gap-x-2">
                                    <span className="text-gray-500 w-32 shrink-0">Scopes</span>
                                    <div className="flex flex-wrap gap-1.5">
                                        {scopeList.map(s => (
                                            <span key={s} className="text-[11px] font-mono bg-white px-2 py-0.5 rounded border border-gray-200 text-gray-700">
                                                {s}
                                            </span>
                                        ))}
                                    </div>
                                </div>
                                {shopifyStatus.installed_at && (
                                    <div className="flex flex-wrap items-baseline gap-x-2">
                                        <span className="text-gray-500 w-32 shrink-0">Connected</span>
                                        <span className="text-gray-900">{new Date(shopifyStatus.installed_at).toLocaleString()}</span>
                                    </div>
                                )}
                                <div className="flex flex-wrap items-baseline gap-x-2">
                                    <span className="text-gray-500 w-32 shrink-0">Sync direction</span>
                                    <span className="inline-flex items-center gap-1.5 text-gray-900 font-medium">
                                        Owntune <ArrowRight className="h-3.5 w-3.5 text-emerald-600" /> Shopify
                                    </span>
                                </div>
                                {shopifyStatus.last_sync_at && (
                                    <div className="flex flex-wrap items-baseline gap-x-2">
                                        <span className="text-gray-500 w-32 shrink-0">Last sync</span>
                                        <span className="text-gray-900">
                                            {new Date(shopifyStatus.last_sync_at).toLocaleString()}
                                            {shopifyStatus.last_sync_status && (
                                                <span className={`ml-2 text-xs ${shopifyStatus.last_sync_status === 'ok' ? 'text-emerald-700' : 'text-red-600'}`}>
                                                    · {shopifyStatus.last_sync_status}
                                                </span>
                                            )}
                                        </span>
                                    </div>
                                )}
                            </div>

                            <div className="mt-5 flex flex-wrap gap-2">
                                <Button
                                    variant="outline"
                                    size="sm"
                                    onClick={handleReconnect}
                                    disabled={connecting}
                                    data-testid="btn-shopify-reconnect"
                                >
                                    <RefreshCw className="h-4 w-4 mr-1.5" />
                                    Reconnect
                                </Button>
                                <a
                                    href={`https://${shopifyStatus.shop_domain}/admin`}
                                    target="_blank"
                                    rel="noreferrer"
                                    className="inline-flex items-center gap-1.5 h-9 px-3 text-sm rounded-md border border-gray-200 hover:bg-gray-50 text-gray-700"
                                    data-testid="btn-open-in-shopify"
                                >
                                    <ExternalLink className="h-4 w-4" />
                                    Open in Shopify
                                </a>
                                <Button
                                    variant="ghost"
                                    size="sm"
                                    onClick={handleDisconnect}
                                    disabled={disconnecting}
                                    className="text-red-500 hover:text-red-600 hover:bg-red-50"
                                    data-testid="btn-shopify-disconnect"
                                >
                                    {disconnecting ? <Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> : <Trash2 className="h-4 w-4 mr-1.5" />}
                                    Disconnect
                                </Button>
                            </div>
                        </div>
                    </div>
                </div>

                <SourceOfTruthBanner />
            </div>
        );
    }

    return (
        <div className="space-y-5 max-w-xl" data-testid="shopify-connect-step">
            <div className="space-y-2">
                <Label className="text-sm text-gray-600">Shopify store URL</Label>
                <Input
                    data-testid="input-store-url"
                    placeholder="your-store.myshopify.com"
                    value={credentials.store_url || ''}
                    onChange={(e) => updateField('store_url', e.target.value)}
                />
                <p className="text-xs text-gray-500">
                    Use the .myshopify.com host (e.g. <code>northlight-records.myshopify.com</code>) — no <code>https://</code> prefix.
                </p>
            </div>
            <div className="pt-2">
                <Button
                    onClick={() => handleConnect()}
                    disabled={connecting}
                    data-testid="btn-shopify-connect"
                    className="bg-[#5E8E3E] hover:bg-[#4a7331] text-white h-11 px-5"
                >
                    {connecting ? (
                        <Loader2 className="h-4 w-4 mr-1.5 animate-spin" />
                    ) : (
                        <ShopifyLogo className="h-5 w-5 mr-2" />
                    )}
                    Connect Shopify
                </Button>
                <p className="text-xs text-gray-500 mt-2">
                    You'll be redirected to Shopify to approve the
                    <code className="mx-1">read_products / read_inventory / write_inventory / read_locations</code>
                    scopes. The access token is stored encrypted server-side and is never exposed to the browser.
                </p>
            </div>
            <SourceOfTruthBanner />
        </div>
    );
};

// Source-of-truth banner (point 3) — used in Step 2, Step 4, near Sync now
const SourceOfTruthBanner = () => (
    <div
        className="rounded-lg border border-indigo-100 bg-indigo-50/60 px-4 py-3 flex items-start gap-3"
        data-testid="source-of-truth-banner"
    >
        <div className="shrink-0 mt-0.5 h-8 w-8 rounded-full bg-white border border-indigo-200 flex items-center justify-center">
            <ArrowRight className="h-4 w-4 text-indigo-600" />
        </div>
        <div className="text-sm text-indigo-900 leading-relaxed">
            <span className="font-semibold">Owntune is the source of truth.</span>{' '}
            Syncing pushes inventory quantities <span className="font-semibold">FROM Owntune TO Shopify</span> —
            no data flows in the reverse direction.
        </div>
    </div>
);

const StepConnect = ({ provider, credentials, onChange, shopifyStatus, setShopifyStatus, isPreviewEnv }) => {
    const updateField = (field, value) => onChange({ ...credentials, [field]: value });

    const providerLabel = PROVIDERS.find(p => p.id === provider)?.name
        || ALT_METHODS.find(m => m.id === provider)?.name
        || provider;

    return (
        <div className="space-y-6 max-w-xl" data-testid="step-connect">
            <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wider">
                Connect {providerLabel}
            </h3>

            {provider === 'woocommerce' && (
                <div className="space-y-4">
                    <div className="space-y-2">
                        <Label className="text-sm text-gray-600">WooCommerce store URL</Label>
                        <Input
                            data-testid="input-store-url"
                            placeholder="your-store.com"
                            value={credentials.store_url || ''}
                            onChange={(e) => updateField('store_url', e.target.value)}
                        />
                    </div>
                    <div className="space-y-2">
                        <Label className="text-sm text-gray-600">API key</Label>
                        <Input
                            data-testid="input-api-key"
                            type="password"
                            placeholder="Enter your API key"
                            value={credentials.api_key || ''}
                            onChange={(e) => updateField('api_key', e.target.value)}
                        />
                    </div>
                </div>
            )}

            {provider === 'shopify' && (
                <ShopifyConnectStep
                    credentials={credentials}
                    updateField={updateField}
                    shopifyStatus={shopifyStatus}
                    setShopifyStatus={setShopifyStatus}
                    isPreviewEnv={isPreviewEnv}
                />
            )}

            {provider === 'linnworks' && (
                <div className="space-y-4">
                    <div className="space-y-2">
                        <Label className="text-sm text-gray-600">Linnworks API key</Label>
                        <Input
                            data-testid="input-api-key"
                            type="password"
                            placeholder="Enter your Linnworks API key"
                            value={credentials.api_key || ''}
                            onChange={(e) => updateField('api_key', e.target.value)}
                        />
                    </div>
                </div>
            )}

            {provider === 'google_sheets' && (
                <div className="space-y-4">
                    <div className="space-y-2">
                        <Label className="text-sm text-gray-600">Spreadsheet ID</Label>
                        <Input
                            data-testid="input-spreadsheet-id"
                            placeholder="e.g. 1BxiMVs0XRA5nFMdKvBdBZjgmUUqptlbs74OgVE2upms"
                            value={credentials.spreadsheet_id || ''}
                            onChange={(e) => updateField('spreadsheet_id', e.target.value)}
                        />
                    </div>
                    <div className="space-y-2">
                        <Label className="text-sm text-gray-600">Sheet name (optional)</Label>
                        <Input
                            data-testid="input-sheet-name"
                            placeholder="Sheet1"
                            value={credentials.sheet_name || ''}
                            onChange={(e) => updateField('sheet_name', e.target.value)}
                        />
                    </div>
                </div>
            )}

            {provider === 'rest_api' && (
                <div className="space-y-4">
                    <div className="space-y-2">
                        <Label className="text-sm text-gray-600">Webhook URL (incoming)</Label>
                        <Input
                            data-testid="input-webhook-url"
                            placeholder="https://your-api.com/webhook/inventory"
                            value={credentials.webhook_url || ''}
                            onChange={(e) => updateField('webhook_url', e.target.value)}
                        />
                    </div>
                    <div className="space-y-2">
                        <Label className="text-sm text-gray-600">Polling endpoint (outgoing)</Label>
                        <Input
                            data-testid="input-polling-endpoint"
                            placeholder="https://your-api.com/api/inventory"
                            value={credentials.polling_endpoint || ''}
                            onChange={(e) => updateField('polling_endpoint', e.target.value)}
                        />
                    </div>
                    <div className="space-y-2">
                        <Label className="text-sm text-gray-600">API key (if required)</Label>
                        <Input
                            data-testid="input-api-key"
                            type="password"
                            placeholder="Optional"
                            value={credentials.api_key || ''}
                            onChange={(e) => updateField('api_key', e.target.value)}
                        />
                    </div>
                </div>
            )}

            {provider === 'csv' && (
                <div className="rounded-xl border border-dashed border-gray-200 p-8 text-center">
                    <FileUp className="h-10 w-10 text-gray-300 mx-auto mb-3" />
                    <p className="text-sm text-gray-500">
                        CSV import uses the existing import feature in Catalogue Library.
                    </p>
                    <p className="text-xs text-gray-400 mt-1">
                        No connection credentials needed. You can configure field mappings and rules on the next steps.
                    </p>
                </div>
            )}

            {provider === 'owntune' && (
                <div className="rounded-xl border border-dashed border-gray-200 p-8 text-center">
                    <Package className="h-10 w-10 text-gray-300 mx-auto mb-3" />
                    <p className="text-sm text-gray-500">
                        Track stock directly in Owntune using the Catalogue Library.
                    </p>
                    <p className="text-xs text-gray-400 mt-1">
                        No external connection needed. Stock levels are managed manually via the catalogue.
                    </p>
                </div>
            )}
        </div>
    );
};

// ==================== Step 3: Match Fields ====================

const StepMatchFields = ({ provider, mappings, onChange }) => {
    const toggleMapping = (idx) => {
        const updated = [...mappings];
        updated[idx] = { ...updated[idx], enabled: !updated[idx].enabled };
        onChange(updated);
    };

    const updateSourceField = (idx, val) => {
        const updated = [...mappings];
        updated[idx] = { ...updated[idx], source_field: val };
        onChange(updated);
    };

    // Shopify: SKU-only auto-matching — no manual mapping needed
    if (provider === 'shopify') {
        return (
            <div className="space-y-6" data-testid="step-match-fields">
                <div className="rounded-2xl border border-gray-200 bg-gray-50/60 p-8 text-center">
                    <div className="mx-auto w-12 h-12 rounded-xl bg-emerald-100 flex items-center justify-center mb-4">
                        <Check className="h-6 w-6 text-emerald-600" />
                    </div>
                    <h3 className="text-base font-semibold text-gray-900 mb-2">
                        Shopify variants are automatically matched using SKU
                    </h3>
                    <p className="text-sm text-gray-500 max-w-md mx-auto">
                        No manual field mapping required.
                    </p>
                    <div className="mt-6 inline-flex items-center gap-3 bg-white rounded-lg border border-gray-200 px-4 py-3 text-sm">
                        <code className="font-mono text-emerald-700 font-semibold">Shopify variant SKU</code>
                        <ArrowRight className="h-4 w-4 text-gray-400" />
                        <div className="text-left">
                            <div className="text-gray-700 font-medium">Owntune</div>
                            <div className="text-xs text-gray-500">
                                catalogue_number <span className="text-gray-400">or</span> sku
                            </div>
                        </div>
                    </div>
                    <div className="mt-5 inline-flex items-center gap-2 text-xs text-gray-500">
                        <span className="text-gray-400">Example:</span>
                        <code className="font-mono bg-white px-2 py-1 rounded border border-gray-200">NLR-001-LP</code>
                        <ArrowRight className="h-3 w-3 text-gray-400" />
                        <code className="font-mono bg-white px-2 py-1 rounded border border-gray-200">NLR-001-LP</code>
                    </div>
                    <p className="text-xs text-gray-400 mt-5">
                        Matching is case-insensitive. Variants without a matching SKU in Owntune are reported as
                        <code className="mx-1 font-mono bg-white px-1.5 py-0.5 rounded border border-gray-200">unmatched</code>
                        in the sync log.
                    </p>
                </div>
            </div>
        );
    }

    return (
        <div className="space-y-6" data-testid="step-match-fields">
            <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wider">
                Map source fields to Owntune
            </h3>
            <p className="text-sm text-gray-400">
                Choose which fields from your source map to Owntune catalogue fields. Toggle off fields you don't need.
            </p>
            <div className="space-y-2">
                {/* Header */}
                <div className="grid grid-cols-[1fr_32px_1fr_48px] gap-3 px-3 py-2">
                    <span className="text-xs font-semibold text-gray-400 uppercase">Source field</span>
                    <span />
                    <span className="text-xs font-semibold text-gray-400 uppercase">Owntune field</span>
                    <span className="text-xs font-semibold text-gray-400 uppercase text-center">On</span>
                </div>
                {/* Rows */}
                {mappings.map((m, i) => {
                    const owntuneLabel = OWNTUNE_FIELDS.find(f => f.key === m.target_field)?.label || m.target_field;
                    return (
                        <div
                            key={i}
                            data-testid={`mapping-row-${i}`}
                            className={`grid grid-cols-[1fr_32px_1fr_48px] gap-3 items-center px-3 py-2.5 rounded-lg border transition-all
                                ${m.enabled ? 'bg-white border-gray-100' : 'bg-gray-50/50 border-gray-50 opacity-50'}
                            `}
                        >
                            <Input
                                value={m.source_field}
                                onChange={(e) => updateSourceField(i, e.target.value)}
                                className="h-8 text-sm"
                                disabled={!m.enabled}
                            />
                            <div className="flex justify-center">
                                <ArrowRight className="h-4 w-4 text-gray-300" />
                            </div>
                            <div className="text-sm text-gray-600 font-medium">{owntuneLabel}</div>
                            <div className="flex justify-center">
                                <Switch
                                    checked={m.enabled}
                                    onCheckedChange={() => toggleMapping(i)}
                                    data-testid={`mapping-toggle-${i}`}
                                />
                            </div>
                        </div>
                    );
                })}
            </div>
        </div>
    );
};

// ==================== Step 4: Rules ====================

const StepRules = ({ rules, onChange, provider }) => {
    const update = (field, value) => onChange({ ...rules, [field]: value });

    return (
        <div className="space-y-8" data-testid="step-rules">
            <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wider">
                Synchronization rules
            </h3>

            {provider === 'shopify' && <SourceOfTruthBanner />}

            <div className="space-y-6">
                <div className="rounded-xl bg-amber-50/60 border border-amber-100 p-5 space-y-2">
                    <div className="flex items-center gap-4">
                        <div className="flex-1">
                            <Label className="text-sm font-semibold text-gray-700">Low stock threshold</Label>
                            <p className="text-xs text-gray-500 mt-0.5">
                                When stock reaches this level, Owntune can trigger alerts or purchase workflows.
                            </p>
                        </div>
                        <div className="w-32">
                            <Input
                                data-testid="input-low-stock-threshold"
                                type="number"
                                min="0"
                                value={rules.low_stock_threshold ?? 50}
                                onChange={(e) => update('low_stock_threshold', parseInt(e.target.value) || 0)}
                                className="h-10"
                            />
                        </div>
                        <div className="shrink-0 text-sm text-gray-500 w-24">units per title</div>
                    </div>
                </div>

                <div className="rounded-xl bg-amber-50/60 border border-amber-100 p-5 space-y-2">
                    <div className="flex items-center gap-4">
                        <div className="flex-1">
                            <Label className="text-sm font-semibold text-gray-700">Auto-sync interval</Label>
                            <p className="text-xs text-gray-500 mt-0.5">
                                How often Owntune automatically pushes inventory updates to{' '}
                                {provider === 'shopify' ? 'Shopify' : 'the connected source'}.
                            </p>
                        </div>
                        <div className="w-32">
                            <Input
                                data-testid="input-auto-sync-hours"
                                type="number"
                                min="1"
                                value={rules.auto_sync_hours ?? 6}
                                onChange={(e) => update('auto_sync_hours', parseInt(e.target.value) || 1)}
                                className="h-10"
                            />
                        </div>
                        <div className="shrink-0 text-sm text-gray-500 w-24">hours</div>
                    </div>
                </div>
            </div>
        </div>
    );
};

// ==================== Active Integration Card (operational dashboard) ====================

const ActiveShopifyCard = ({
    shopifyStatus,
    onSyncNow,
    onEdit,
    onDisconnect,
    onReconnect,
    syncing,
    lastSyncResult,
    isPreviewEnv,
}) => {
    const scopeList = (shopifyStatus.scopes || 'read_products,read_inventory,write_inventory,read_locations')
        .split(',').map(s => s.trim()).filter(Boolean);
    const summary = shopifyStatus.last_sync_summary || {};
    const productsSynced =
        lastSyncResult?.updated_count ??
        summary.updated_count ??
        summary.matched_count ??
        null;
    const isHealthy = shopifyStatus.last_sync_status !== 'errors' && shopifyStatus.connected;

    return (
        <div
            className="rounded-2xl border border-gray-200 bg-white shadow-sm overflow-hidden"
            data-testid="active-shopify-card"
        >
            {/* Top status strip */}
            <div className="px-7 py-5 border-b border-gray-100 flex items-start justify-between gap-6 flex-wrap bg-gradient-to-br from-emerald-50/40 to-white">
                <div className="flex items-start gap-4 min-w-0">
                    <div className="shrink-0 w-12 h-12 rounded-xl bg-white border border-emerald-200 shadow-sm flex items-center justify-center">
                        <ShopifyLogo className="h-7 w-7" />
                    </div>
                    <div className="min-w-0">
                        <div className="flex items-center gap-2 flex-wrap">
                            <span className={`inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full text-[11px] font-semibold ${
                                isHealthy ? 'bg-emerald-100 text-emerald-800' : 'bg-amber-100 text-amber-800'
                            }`}>
                                <span className={`h-1.5 w-1.5 rounded-full ${isHealthy ? 'bg-emerald-500' : 'bg-amber-500'} animate-pulse`} />
                                {isHealthy ? 'Connected · Healthy' : 'Connected · Needs attention'}
                            </span>
                            {isPreviewEnv && (
                                <span className="text-[10px] font-semibold uppercase tracking-wide px-1.5 py-0.5 rounded bg-amber-100 text-amber-800 border border-amber-200" data-testid="preview-env-badge">
                                    Preview env
                                </span>
                            )}
                        </div>
                        <div className="text-lg font-semibold text-gray-900 mt-1.5">Shopify</div>
                        <div className="font-mono text-sm text-gray-700 truncate">
                            {shopifyStatus.shop_domain}
                        </div>
                    </div>
                </div>
                <div className="flex items-center gap-2 flex-wrap">
                    <Button
                        onClick={onSyncNow}
                        disabled={syncing}
                        size="sm"
                        className="bg-[#5E8E3E] hover:bg-[#4a7331] text-white"
                        data-testid="btn-sync-now"
                    >
                        {syncing ? <Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> : <RefreshCw className="h-4 w-4 mr-1.5" />}
                        Sync now
                    </Button>
                    <a
                        href={`https://${shopifyStatus.shop_domain}/admin`}
                        target="_blank"
                        rel="noreferrer"
                        className="inline-flex items-center gap-1.5 h-9 px-3 text-sm rounded-md border border-gray-200 hover:bg-gray-50 text-gray-700"
                        data-testid="btn-open-in-shopify"
                    >
                        <ExternalLink className="h-4 w-4" />
                        Open Shopify
                    </a>
                    <Button
                        variant="outline"
                        size="sm"
                        onClick={onEdit}
                        data-testid="btn-edit-integration"
                    >
                        <Settings2 className="h-4 w-4 mr-1.5" />
                        Settings
                    </Button>
                    <Button
                        variant="ghost"
                        size="sm"
                        onClick={onDisconnect}
                        className="text-red-500 hover:text-red-600 hover:bg-red-50"
                        data-testid="btn-shopify-disconnect"
                    >
                        <Trash2 className="h-4 w-4 mr-1.5" />
                        Disconnect
                    </Button>
                </div>
            </div>

            {/* Stats grid */}
            <div className="px-7 py-5 grid grid-cols-2 sm:grid-cols-4 gap-6 border-b border-gray-100">
                <div>
                    <div className="text-[11px] font-semibold uppercase tracking-wider text-gray-500">Sync direction</div>
                    <div className="mt-1.5 inline-flex items-center gap-1.5 text-sm font-medium text-gray-900">
                        Owntune <ArrowRight className="h-3.5 w-3.5 text-emerald-600" /> Shopify
                    </div>
                </div>
                <div>
                    <div className="text-[11px] font-semibold uppercase tracking-wider text-gray-500">Last synced</div>
                    <div className="mt-1.5 text-sm font-medium text-gray-900">
                        {shopifyStatus.last_sync_at
                            ? new Date(shopifyStatus.last_sync_at).toLocaleString()
                            : <span className="text-gray-400 font-normal">Never</span>}
                    </div>
                </div>
                <div>
                    <div className="text-[11px] font-semibold uppercase tracking-wider text-gray-500">Products synced</div>
                    <div className="mt-1.5 text-sm font-medium text-gray-900">
                        {productsSynced != null ? productsSynced : <span className="text-gray-400 font-normal">—</span>}
                    </div>
                </div>
                <div>
                    <div className="text-[11px] font-semibold uppercase tracking-wider text-gray-500">Connected</div>
                    <div className="mt-1.5 text-sm font-medium text-gray-900">
                        {shopifyStatus.installed_at
                            ? new Date(shopifyStatus.installed_at).toLocaleDateString()
                            : <span className="text-gray-400 font-normal">—</span>}
                    </div>
                </div>
            </div>

            {/* Scopes & sub-actions */}
            <div className="px-7 py-4 flex items-start justify-between gap-6 flex-wrap">
                <div>
                    <div className="text-[11px] font-semibold uppercase tracking-wider text-gray-500 mb-2">Granted scopes</div>
                    <div className="flex flex-wrap gap-1.5">
                        {scopeList.map(s => (
                            <span key={s} className="text-[11px] font-mono bg-gray-50 px-2 py-0.5 rounded border border-gray-200 text-gray-700">
                                {s}
                            </span>
                        ))}
                    </div>
                </div>
                <button
                    onClick={onReconnect}
                    className="text-xs text-gray-500 hover:text-gray-700 underline-offset-4 hover:underline"
                    data-testid="btn-shopify-reconnect"
                >
                    Reconnect / change store
                </button>
            </div>
        </div>
    );
};

const ActiveIntegrationsSection = ({
    shopifyStatus,
    onSyncNow,
    onEdit,
    onDisconnect,
    onReconnect,
    syncing,
    lastSyncResult,
    isPreviewEnv,
}) => (
    <section className="space-y-3" data-testid="active-integrations-section">
        <div className="flex items-end justify-between">
            <div>
                <h2 className="text-lg font-semibold text-gray-900">Active integrations</h2>
                <p className="text-sm text-gray-500">
                    Connected inventory systems pushing data to and from Owntune.
                </p>
            </div>
        </div>
        <ActiveShopifyCard
            shopifyStatus={shopifyStatus}
            onSyncNow={onSyncNow}
            onEdit={onEdit}
            onDisconnect={onDisconnect}
            onReconnect={onReconnect}
            syncing={syncing}
            lastSyncResult={lastSyncResult}
            isPreviewEnv={isPreviewEnv}
        />
    </section>
);

const AddIntegrationSection = ({ availableProviders, onChoose }) => (
    <section className="space-y-3" data-testid="add-integration-section">
        <div className="flex items-end justify-between">
            <div>
                <h2 className="text-lg font-semibold text-gray-900">Add another integration</h2>
                <p className="text-sm text-gray-500">
                    Connect another inventory source. Each integration syncs independently.
                </p>
            </div>
        </div>
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4">
            {availableProviders.map(p => (
                <button
                    key={p.id}
                    onClick={() => onChoose(p.id)}
                    data-testid={`add-provider-${p.id}`}
                    className="flex items-center gap-3 p-4 rounded-xl border border-gray-200 bg-white hover:border-gray-300 hover:shadow-sm transition-all text-left group"
                >
                    <div className={`w-10 h-10 rounded-lg flex items-center justify-center text-sm font-bold shrink-0 ${p.color}`}>
                        {p.initials}
                    </div>
                    <div className="min-w-0 flex-1">
                        <div className="font-semibold text-sm text-gray-900 whitespace-nowrap">{p.name}</div>
                        <div className="text-xs text-gray-400 whitespace-nowrap">{p.desc}</div>
                    </div>
                    <Plus className="h-4 w-4 text-gray-400 group-hover:text-gray-600 transition-colors" />
                </button>
            ))}
            {ALT_METHODS.map(m => {
                const Icon = m.icon;
                return (
                    <button
                        key={m.id}
                        onClick={() => onChoose(m.id)}
                        data-testid={`add-provider-${m.id}`}
                        className="flex items-center gap-3 p-4 rounded-xl border border-gray-200 bg-white hover:border-gray-300 hover:shadow-sm transition-all text-left group"
                    >
                        <div className="w-10 h-10 rounded-lg bg-gray-50 flex items-center justify-center shrink-0">
                            <Icon className="h-5 w-5 text-gray-500" />
                        </div>
                        <div className="min-w-0 flex-1">
                            <div className="font-semibold text-sm text-gray-900 whitespace-nowrap">{m.name}</div>
                            <div className="text-xs text-gray-400 whitespace-nowrap">{m.desc}</div>
                        </div>
                        <Plus className="h-4 w-4 text-gray-400 group-hover:text-gray-600 transition-colors" />
                    </button>
                );
            })}
        </div>
    </section>
);

// Sync result card (shown briefly after Sync now)
const SyncResultCard = ({ result, onDismiss }) => (
    <div
        className={`rounded-2xl border-2 p-6 ${
            result.ok ? 'bg-emerald-50/60 border-emerald-200' : 'bg-red-50/60 border-red-200'
        }`}
        data-testid="sync-result-card"
    >
        <div className="flex items-start gap-3">
            <div className={`shrink-0 w-10 h-10 rounded-full flex items-center justify-center ${
                result.ok ? 'bg-emerald-500 text-white' : 'bg-red-500 text-white'
            }`}>
                {result.ok ? <Check className="h-5 w-5" /> : <AlertCircle className="h-5 w-5" />}
            </div>
            <div className="flex-1 min-w-0">
                <div className="flex items-center justify-between gap-3">
                    <h3 className="text-base font-semibold text-gray-900">
                        {result.ok ? 'Sync complete' : 'Sync finished with errors'}
                    </h3>
                    {onDismiss && (
                        <button
                            onClick={onDismiss}
                            className="text-xs text-gray-400 hover:text-gray-600"
                            data-testid="btn-dismiss-sync-result"
                        >
                            Dismiss
                        </button>
                    )}
                </div>
                <p className="text-xs text-gray-500 mt-0.5">
                    {result.finished_at && new Date(result.finished_at).toLocaleString()}
                    {' · '}{(result.duration_ms / 1000).toFixed(2)}s
                </p>
                <div className="mt-4 grid grid-cols-2 sm:grid-cols-4 gap-4 text-sm">
                    <div>
                        <div className="text-gray-500 text-xs uppercase tracking-wide">Variants pushed</div>
                        <div className="text-2xl font-semibold text-gray-900 mt-0.5">{result.updated_count}</div>
                    </div>
                    <div>
                        <div className="text-gray-500 text-xs uppercase tracking-wide">Matched by SKU</div>
                        <div className="text-2xl font-semibold text-gray-900 mt-0.5">{result.matched_count}</div>
                    </div>
                    <div>
                        <div className="text-gray-500 text-xs uppercase tracking-wide">Unmatched SKUs</div>
                        <div className="text-2xl font-semibold text-gray-900 mt-0.5">{result.unmatched_count}</div>
                    </div>
                    <div>
                        <div className="text-gray-500 text-xs uppercase tracking-wide">Variants seen</div>
                        <div className="text-2xl font-semibold text-gray-900 mt-0.5">{result.variants_seen}</div>
                    </div>
                </div>
                {result.unmatched_count > 0 && result.unmatched_skus.length > 0 && (
                    <div className="mt-4 pt-4 border-t border-emerald-200/60">
                        <div className="text-xs text-gray-500 uppercase tracking-wide mb-2">
                            Unmatched SKUs (first {Math.min(10, result.unmatched_skus.length)})
                        </div>
                        <div className="flex flex-wrap gap-1.5">
                            {result.unmatched_skus.slice(0, 10).map(sku => (
                                <code key={sku} className="text-[11px] font-mono bg-white px-2 py-0.5 rounded border border-gray-200 text-gray-700">
                                    {sku}
                                </code>
                            ))}
                            {result.unmatched_skus.length > 10 && (
                                <span className="text-[11px] text-gray-500 self-center">
                                    +{result.unmatched_skus.length - 10} more
                                </span>
                            )}
                        </div>
                    </div>
                )}
                {result.errors.length > 0 && (
                    <div className="mt-4 pt-4 border-t border-red-200/60">
                        <div className="text-xs text-red-700 uppercase tracking-wide mb-2 font-semibold">
                            Errors ({result.errors.length})
                        </div>
                        <ul className="text-xs text-red-700 space-y-1 list-disc list-inside">
                            {result.errors.slice(0, 5).map((e, i) => (
                                <li key={i} className="break-all">{e}</li>
                            ))}
                        </ul>
                    </div>
                )}
            </div>
        </div>
    </div>
);

// ==================== Main Page ====================

export default function CatalogueIntegrationsPage() {
    const navigate = useNavigate();
    const { user } = useAuth();
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [testing, setTesting] = useState(false);
    const [step, setStep] = useState(1);
    const [existingIntegration, setExistingIntegration] = useState(null);

    // Form state
    const [provider, setProvider] = useState(null);
    const [credentials, setCredentials] = useState({});
    const [mappings, setMappings] = useState([]);
    const [rules, setRules] = useState({ low_stock_threshold: 50, auto_sync_hours: 6 });
    const [shopifyStatus, setShopifyStatus] = useState(null);
    const [lastSyncResult, setLastSyncResult] = useState(null); // for the result card

    // View mode controls whether the page shows the operational dashboard
    // ('overview') or the 4-step wizard ('wizard'). This is the IA fix:
    // a connected integration should NOT live inside an onboarding wizard.
    const [viewMode, setViewMode] = useState('overview');
    const [wizardEntryPoint, setWizardEntryPoint] = useState('add'); // add | edit | reconnect
    // Preview-environment detection — purely cosmetic badge
    const isPreviewEnv = typeof window !== 'undefined'
        && /preview\.emergentagent\.com$|localhost/.test(window.location.hostname);

    // Load existing integration
    const loadIntegration = useCallback(async () => {
        try {
            const res = await catalogueIntegrationsApi.get();
            const data = res.data;
            if (data) {
                setExistingIntegration(data);
                setProvider(data.provider);
                setCredentials(data.credentials || {});
                setMappings(data.field_mappings || []);
                setRules(data.sync_rules || { low_stock_threshold: 50, auto_sync_hours: 6 });
            }
        } catch (err) {
            // No integration yet
        } finally {
            setLoading(false);
        }
    }, []);

    const loadShopifyStatus = useCallback(async () => {
        try {
            const res = await shopifyApi.status();
            setShopifyStatus(res.data || { connected: false });
        } catch {
            setShopifyStatus({ connected: false });
        }
    }, []);

    useEffect(() => { loadIntegration(); }, [loadIntegration]);
    useEffect(() => { loadShopifyStatus(); }, [loadShopifyStatus]);

    // Auto-decide initial view mode after both loads complete:
    //  - any active integration → overview (operational dashboard)
    //  - none → wizard (onboarding)
    // We only run this once after the initial load to avoid stomping on
    // user-driven mode changes (Settings, Add another, etc).
    const initialModeRef = useRef(false);
    useEffect(() => {
        if (loading || shopifyStatus === null) return;
        if (initialModeRef.current) return;
        initialModeRef.current = true;
        const hasAnyConnected = !!shopifyStatus?.connected || (existingIntegration && existingIntegration.provider !== 'shopify');
        setViewMode(hasAnyConnected ? 'overview' : 'wizard');
    }, [loading, shopifyStatus, existingIntegration]);

    // Connected providers (used to filter out from "Add another" grid)
    const connectedProviderIds = useMemo(() => {
        const ids = new Set();
        if (shopifyStatus?.connected) ids.add('shopify');
        if (existingIntegration && existingIntegration.provider && existingIntegration.provider !== 'shopify') {
            ids.add(existingIntegration.provider);
        }
        return ids;
    }, [shopifyStatus, existingIntegration]);

    const availableProvidersForAdd = useMemo(
        () => PROVIDERS.filter(p => !connectedProviderIds.has(p.id)),
        [connectedProviderIds]
    );

    const enterWizardForAdd = (providerId) => {
        setWizardEntryPoint('add');
        setProvider(providerId);
        setCredentials({});
        loadDefaultMappings(providerId);
        setStep(providerId ? 2 : 1);
        setViewMode('wizard');
    };

    const enterWizardForEdit = () => {
        setWizardEntryPoint('edit');
        // Existing provider data is already loaded in state
        setStep(4); // jump straight to Rules; Steps 1-3 are already settled for connected integration
        setViewMode('wizard');
    };

    const exitWizard = () => {
        setViewMode('overview');
        setStep(1);
    };

    // Load default mappings when provider changes
    const loadDefaultMappings = useCallback(async (provId) => {
        if (!provId || provId === 'csv' || provId === 'owntune') {
            setMappings([]);
            return;
        }
        try {
            const res = await catalogueIntegrationsApi.getDefaultMappings(provId);
            setMappings(res.data || []);
        } catch {
            setMappings([]);
        }
    }, []);

    const handleProviderSelect = (id) => {
        setProvider(id);
        setCredentials({});
        loadDefaultMappings(id);
    };

    const handleNext = () => {
        if (step === 1 && !provider) {
            toast.error('Please select an integration source');
            return;
        }
        if (step < 4) setStep(step + 1);
    };

    const handleBack = () => {
        if (step > 1) setStep(step - 1);
    };

    const handleTestConnection = async () => {
        setTesting(true);
        try {
            // Save first to ensure credentials are persisted
            await catalogueIntegrationsApi.save({
                provider,
                credentials,
                field_mappings: mappings,
                sync_rules: rules,
            });
            const res = await catalogueIntegrationsApi.test();
            if (res.data?.success) {
                toast.success(res.data.message || 'Connection successful');
                setExistingIntegration(prev => ({ ...prev, status: 'connected' }));
            } else {
                toast.error(res.data?.message || 'Connection failed');
            }
        } catch (err) {
            toast.error(err.response?.data?.detail || 'Connection test failed');
        } finally {
            setTesting(false);
        }
    };

    const [syncing, setSyncing] = useState(false);
    const handleSyncNow = async () => {
        setSyncing(true);
        setLastSyncResult(null);
        try {
            // Persist any unsaved Step 3/4 changes first
            if (provider) {
                await catalogueIntegrationsApi.save({
                    provider,
                    credentials: provider === 'shopify' ? {} : credentials,
                    field_mappings: mappings,
                    sync_rules: rules,
                });
            }
            const res = await shopifyApi.sync();
            const r = res.data || {};
            const summary = r.summary || {};
            setLastSyncResult({
                ok: !(summary.errors_count ?? (r.errors || []).length),
                updated_count: summary.updated_count ?? 0,
                matched_count: summary.matched_count ?? 0,
                unmatched_count: summary.unmatched_count ?? 0,
                variants_seen: summary.variants_seen ?? 0,
                errors: r.errors || [],
                duration_ms: summary.duration_ms ?? 0,
                unmatched_skus: r.unmatched_skus || [],
                finished_at: r.finished_at,
            });
            const updated = summary.updated_count ?? 0;
            const errs = summary.errors_count ?? (r.errors || []).length;
            if (errs) {
                toast.error(`Sync finished with ${errs} error(s) — pushed ${updated}`);
            } else {
                toast.success(`Sync complete — ${updated} variants pushed to Shopify`);
            }
            await loadShopifyStatus();
        } catch (err) {
            toast.error(err.response?.data?.detail || 'Sync failed');
        } finally {
            setSyncing(false);
        }
    };

    const handleSave = async () => {
        setSaving(true);
        try {
            await catalogueIntegrationsApi.save({
                provider,
                credentials: provider === 'shopify' ? {} : credentials,
                field_mappings: mappings,
                sync_rules: rules,
            });
            toast.success('Integration saved successfully');
            // After saving, return to operational dashboard rather than
            // jumping to /catalogue or staying in the wizard.
            await Promise.all([loadIntegration(), loadShopifyStatus()]);
            exitWizard();
        } catch (err) {
            toast.error('Failed to save integration');
        } finally {
            setSaving(false);
        }
    };

    const handleRemove = async () => {
        if (!window.confirm('Are you sure you want to remove this integration?')) return;
        try {
            await catalogueIntegrationsApi.remove();
            toast.success('Integration removed');
            setExistingIntegration(null);
            setProvider(null);
            setCredentials({});
            setMappings([]);
            setRules({ low_stock_threshold: 50, auto_sync_hours: 6 });
            setStep(1);
        } catch (err) {
            toast.error('Failed to remove integration');
        }
    };

    const handleDisconnectShopify = async () => {
        if (!window.confirm('Disconnect Shopify? You will need to re-authorize to sync inventory again.')) return;
        try {
            await shopifyApi.disconnect();
            toast.success('Shopify disconnected');
            setShopifyStatus({ connected: false });
            setLastSyncResult(null);
            await loadIntegration();
            // If no other integrations remain, go back to wizard for a fresh setup
            setViewMode('wizard');
            setProvider(null);
            setCredentials({});
            setStep(1);
        } catch (err) {
            toast.error(err.response?.data?.detail || 'Failed to disconnect');
        }
    };

    const handleReconnectShopify = async () => {
        if (!shopifyStatus?.shop_domain) return;
        try {
            const res = await shopifyApi.oauthInit(shopifyStatus.shop_domain);
            const { authorize_url } = res.data || {};
            if (authorize_url) {
                sessionStorage.setItem('shopify_oauth_shop', shopifyStatus.shop_domain);
                window.location.href = authorize_url;
            }
        } catch (e) {
            toast.error(e.response?.data?.detail || 'Could not start reconnect');
        }
    };

    const providerInfo = PROVIDERS.find(p => p.id === provider)
        || ALT_METHODS.find(m => m.id === provider);

    const statusLabel = existingIntegration?.status === 'connected'
        ? `${providerInfo?.name || provider} connected`
        : provider ? `${providerInfo?.name || provider} selected` : 'No source selected';

    const statusColor = existingIntegration?.status === 'connected' ? 'text-emerald-500' : 'text-gray-400';

    if (loading) {
        return (
            <div className="min-h-screen bg-gray-50/50">
                <Header />
                <div className="flex items-center justify-center h-[60vh]">
                    <LoadingSpinner />
                </div>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-gray-50/50" data-testid="catalogue-integrations-page">
            <Header />
            <div className="max-w-5xl mx-auto px-6 py-10">
                {/* Page header */}
                <div className="mb-8">
                    <button
                        onClick={() => navigate('/catalogue')}
                        className="flex items-center gap-1 text-sm text-gray-400 hover:text-gray-600 mb-4 transition-colors"
                        data-testid="back-to-catalogue"
                    >
                        <ArrowLeft className="h-4 w-4" /> Back to Catalogue Library
                    </button>
                    <h1 className="text-2xl font-bold text-gray-900" data-testid="page-title">Stock Integration</h1>
                    <p className="text-sm text-gray-400 mt-1">
                        Connect Owntune with your inventory systems and push stock quantities to your sales channels.
                    </p>
                </div>

                {/* OVERVIEW MODE — operational dashboard */}
                {viewMode === 'overview' && (
                    <div className="space-y-10" data-testid="integrations-overview">
                        {shopifyStatus?.connected ? (
                            <>
                                <ActiveIntegrationsSection
                                    shopifyStatus={shopifyStatus}
                                    onSyncNow={handleSyncNow}
                                    onEdit={enterWizardForEdit}
                                    onDisconnect={handleDisconnectShopify}
                                    onReconnect={handleReconnectShopify}
                                    syncing={syncing}
                                    lastSyncResult={lastSyncResult}
                                    isPreviewEnv={isPreviewEnv}
                                />
                                {lastSyncResult && (
                                    <SyncResultCard result={lastSyncResult} onDismiss={() => setLastSyncResult(null)} />
                                )}
                                {availableProvidersForAdd.length > 0 && (
                                    <AddIntegrationSection
                                        availableProviders={availableProvidersForAdd}
                                        onChoose={enterWizardForAdd}
                                    />
                                )}
                            </>
                        ) : (
                            // No active integrations — render the wizard from scratch
                            // (this branch is rarely hit because initial-mode effect
                            // already routes empty-state users into wizard mode)
                            <div className="text-center py-20 text-gray-400">
                                <Package className="h-10 w-10 mx-auto mb-3 text-gray-300" />
                                <p className="text-sm">No integrations connected.</p>
                                <Button
                                    onClick={() => enterWizardForAdd(null)}
                                    className="mt-4 bg-indigo-600 hover:bg-indigo-700 text-white"
                                    data-testid="btn-overview-add-integration"
                                >
                                    <Plus className="h-4 w-4 mr-1.5" /> Add integration
                                </Button>
                            </div>
                        )}
                    </div>
                )}

                {/* WIZARD MODE — onboarding flow (only when adding/editing) */}
                {viewMode === 'wizard' && (
                    <div data-testid="integrations-wizard">
                        {/* Wizard header — clarifies that this is a transient flow */}
                        {(shopifyStatus?.connected || existingIntegration) && (
                            <div className="mb-6 flex items-center justify-between gap-3 px-4 py-3 rounded-lg bg-indigo-50 border border-indigo-100">
                                <div className="text-sm text-indigo-900">
                                    <span className="font-semibold">
                                        {wizardEntryPoint === 'edit' ? 'Editing integration' : 'Adding integration'}
                                    </span>
                                    {' — '}
                                    {wizardEntryPoint === 'edit'
                                        ? 'changes apply to your active integration.'
                                        : 'configure a new inventory source. Existing integrations stay connected.'}
                                </div>
                                <Button
                                    variant="ghost"
                                    size="sm"
                                    onClick={exitWizard}
                                    data-testid="btn-back-to-overview"
                                    className="text-indigo-700 hover:bg-indigo-100"
                                >
                                    <ArrowLeft className="h-4 w-4 mr-1" /> Back to integrations
                                </Button>
                            </div>
                        )}

                        <Stepper currentStep={step} steps={STEP_LABELS} />

                        <div className="min-h-[320px]">
                            {step === 1 && (
                                <StepChooseSource selected={provider} onSelect={handleProviderSelect} />
                            )}
                            {step === 2 && (
                                <StepConnect
                                    provider={provider}
                                    credentials={credentials}
                                    onChange={setCredentials}
                                    shopifyStatus={shopifyStatus}
                                    setShopifyStatus={setShopifyStatus}
                                    isPreviewEnv={isPreviewEnv}
                                />
                            )}
                            {step === 3 && (
                                <StepMatchFields provider={provider} mappings={mappings} onChange={setMappings} />
                            )}
                            {step === 4 && (
                                <StepRules rules={rules} onChange={setRules} provider={provider} />
                            )}
                        </div>

                        {/* Wizard footer — Cancel / Back / Next / Save */}
                        <div className="flex items-center justify-between mt-10 pt-6 border-t border-gray-100">
                            <div className="flex items-center gap-2 text-sm">
                                <CircleDot className={`h-3 w-3 ${statusColor}`} />
                                <span className="text-gray-500">{statusLabel}</span>
                            </div>
                            <div className="flex items-center gap-2">
                                {existingIntegration && wizardEntryPoint === 'edit' && (
                                    <Button
                                        variant="ghost"
                                        size="sm"
                                        onClick={handleRemove}
                                        className="text-red-500 hover:text-red-600 hover:bg-red-50"
                                        data-testid="btn-remove-integration"
                                    >
                                        <Trash2 className="h-4 w-4 mr-1" />
                                        Remove
                                    </Button>
                                )}
                                <Button
                                    variant="outline"
                                    size="sm"
                                    onClick={step === 1 ? exitWizard : handleBack}
                                    data-testid="btn-cancel"
                                >
                                    {step === 1 ? 'Cancel' : 'Back'}
                                </Button>
                                {step >= 2 && provider !== 'csv' && provider !== 'shopify' && (
                                    <Button
                                        variant="outline"
                                        size="sm"
                                        onClick={handleTestConnection}
                                        disabled={testing}
                                        data-testid="btn-test-connection"
                                    >
                                        {testing ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <ExternalLink className="h-4 w-4 mr-1" />}
                                        Test connection
                                    </Button>
                                )}
                                {step < 4 ? (
                                    <Button
                                        size="sm"
                                        onClick={handleNext}
                                        disabled={step === 1 && !provider}
                                        data-testid="btn-next-step"
                                        className="bg-indigo-600 hover:bg-indigo-700 text-white"
                                    >
                                        Next step
                                        <ArrowRight className="h-4 w-4 ml-1" />
                                    </Button>
                                ) : (
                                    <Button
                                        size="sm"
                                        onClick={handleSave}
                                        disabled={saving}
                                        data-testid="btn-save-integration"
                                        className="bg-indigo-600 hover:bg-indigo-700 text-white"
                                    >
                                        {saving ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <Check className="h-4 w-4 mr-1" />}
                                        Save integration
                                    </Button>
                                )}
                            </div>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
}
